import React, { useState, useEffect, memo } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box, Flex, Text, VStack, HStack, Switch, FormControl, FormLabel,
  ButtonGroup, Button, CircularProgress, CircularProgressLabel
} from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { scoresSelector } from '../../state/calculatorState';

// Company Colors
const COLORS = {
  black: '#000000',
  white: '#ffffff',
  ltGray1: '#f2f2f2',
  ltGray2: '#e8e8e8',
  dkGray1: '#c8c8c8',
  dkGray2: '#4c4d4f',
  ltRed: '#cf102d',
  dkRed: '#ab192d',
  ltPurple: '#b588b9',
  dkPurple: '#94368d',
  ltBlue: '#3db5e6',
  dkBlue: '#006aa7',
  ltGreen: '#6bc04b',
  dkGreen: '#009946',
  ltYellow: '#f3ce00',
  dkYellow: '#d08a00'
};

const SITE_COLOR = COLORS.dkRed;
const WELL_COLOR = COLORS.dkBlue;

const CHART_STORAGE_KEY = 'scoreChart_chartType';
const ANIMATION_STORAGE_KEY = 'scoreChart_animationsEnabled';

const Dial = ({ value, label, color, size = 60 }) => {
  const percentage = Math.round(value * 100);
  return (
    <VStack spacing={2}>
      <CircularProgress
        value={percentage}
        size={`${size}px`}
        thickness="8px"
        color={color}
        trackColor={COLORS.ltGray2}
        capIsRound
      >
        <CircularProgressLabel fontSize="md" fontWeight="700" color={color}>
          {percentage}%
        </CircularProgressLabel>
      </CircularProgress>
      <Text fontSize="sm" color={COLORS.dkGray2} fontWeight="500" textAlign="center">
        {label}
      </Text>
    </VStack>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box bg={COLORS.black} px={3} py={2} borderRadius="md" boxShadow="lg" lineHeight="1.3">
        <Text color={COLORS.white} fontWeight="600" fontSize="sm">{label}</Text>
        {payload.map((entry, index) => (
          <Text key={index} color={COLORS.white} fontSize="sm">
            {entry.name}: {Math.round(entry.value * 100)}%
          </Text>
        ))}
      </Box>
    );
  }
  return null;
};

export const ScoreChart = memo(() => {
  const scores = useRecoilValue(scoresSelector);
  const totalScore = (scores.siteScore + scores.wellScore) / 2;
  const totalPercentage = Math.round(totalScore * 100);

  const [chartType, setChartType] = useState(() => {
    return localStorage.getItem(CHART_STORAGE_KEY) || 'bars';
  });

  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const stored = localStorage.getItem(ANIMATION_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(CHART_STORAGE_KEY, chartType);
  }, [chartType]);

  useEffect(() => {
    localStorage.setItem(ANIMATION_STORAGE_KEY, String(animationsEnabled));
  }, [animationsEnabled]);

  const getScoreColor = (score) => {
    if (score < 0.33) return COLORS.dkGreen;
    if (score < 0.66) return COLORS.dkYellow;
    return COLORS.dkRed;
  };

  // Data for bar chart
  const barData = [
    { name: 'Location', value: scores.locationScore, type: 'site' },
    { name: 'Rig Cap.', value: scores.rigScore, type: 'site' },
    { name: 'Design', value: scores.dsgnScore, type: 'well' },
    { name: 'Construction', value: scores.cnstrctnScore, type: 'well' },
    { name: 'Drilling', value: scores.drlgCmplxScore, type: 'well' },
    { name: 'Well Ctrl', value: scores.wllCtrlScore, type: 'well' }
  ];

  // Data for radar chart
  const radarData = [
    { category: 'Location', value: scores.locationScore, fullMark: 1 },
    { category: 'Rig Cap.', value: scores.rigScore, fullMark: 1 },
    { category: 'Design', value: scores.dsgnScore, fullMark: 1 },
    { category: 'Construction', value: scores.cnstrctnScore, fullMark: 1 },
    { category: 'Drilling', value: scores.drlgCmplxScore, fullMark: 1 },
    { category: 'Well Ctrl', value: scores.wllCtrlScore, fullMark: 1 }
  ];

  // Data for donut chart
  const donutData = [
    { name: 'Location', value: scores.locationScore, color: COLORS.ltRed },
    { name: 'Rig Cap.', value: scores.rigScore, color: COLORS.dkRed },
    { name: 'Design', value: scores.dsgnScore, color: COLORS.ltBlue },
    { name: 'Construction', value: scores.cnstrctnScore, color: COLORS.dkBlue },
    { name: 'Drilling', value: scores.drlgCmplxScore, color: COLORS.ltPurple },
    { name: 'Well Ctrl', value: scores.wllCtrlScore, color: COLORS.dkPurple }
  ];

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={barData} layout="vertical" margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.ltGray2} horizontal={true} vertical={false} />
        <XAxis
          type="number"
          domain={[0, 1]}
          tickFormatter={(v) => `${v * 100}%`}
          tick={{ fill: COLORS.dkGray2, fontSize: 11 }}
          axisLine={{ stroke: COLORS.ltGray2 }}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fill: COLORS.dkGray2, fontSize: 12, fontWeight: 500 }}
          axisLine={false}
          tickLine={false}
          width={70}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="value"
          radius={[0, 4, 4, 0]}
          isAnimationActive={animationsEnabled}
          animationDuration={300}
          label={{ position: 'right', fill: COLORS.dkGray2, fontSize: 11, fontWeight: 600, formatter: (v) => `${Math.round(v * 100)}%` }}
        >
          {barData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.type === 'site' ? SITE_COLOR : WELL_COLOR} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const renderRadarChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <RadarChart data={radarData} margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
        <PolarGrid stroke={COLORS.ltGray2} />
        <PolarAngleAxis
          dataKey="category"
          tick={{ fill: COLORS.dkGray2, fontSize: 11, fontWeight: 500 }}
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, 1]}
          tick={false}
          axisLine={false}
        />
        <Radar
          name="Complexity"
          dataKey="value"
          stroke={COLORS.dkRed}
          fill={COLORS.ltRed}
          fillOpacity={0.5}
          isAnimationActive={animationsEnabled}
          animationDuration={300}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <Box bg={COLORS.black} px={3} py={2} borderRadius="md" lineHeight="1.3">
                  <Text color={COLORS.white} fontSize="sm">
                    {payload[0].payload.category}: {Math.round(payload[0].value * 100)}%
                  </Text>
                </Box>
              );
            }
            return null;
          }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );

  const renderDonutChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
        <Pie
          data={donutData}
          cx="50%"
          cy="50%"
          innerRadius="50%"
          outerRadius="80%"
          paddingAngle={2}
          dataKey="value"
          isAnimationActive={animationsEnabled}
          animationBegin={0}
          animationDuration={300}
          label={({ name, value }) => `${name}: ${Math.round(value * 100)}%`}
          labelLine={{ stroke: COLORS.dkGray1 }}
        >
          {donutData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <Box bg={COLORS.black} px={3} py={2} borderRadius="md" lineHeight="1.3">
                  <Text color={COLORS.white} fontSize="sm">
                    {payload[0].name}: {Math.round(payload[0].value * 100)}%
                  </Text>
                </Box>
              );
            }
            return null;
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value) => <span style={{ color: COLORS.dkGray2, fontSize: 11 }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );

  return (
    <Box
      bg={COLORS.white}
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      h="calc(95vh - 200px)"
      minH="500px"
    >
      {/* Header with overall score */}
      <Box bg="#3a3b3d" px={8} py={6}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={6}>
          {/* Overall Score */}
          <HStack spacing={6}>
            <Box textAlign="center">
              <Text
                fontSize="6xl"
                fontWeight="800"
                color={getScoreColor(totalScore)}
                lineHeight="1"
              >
                {totalPercentage}
              </Text>
              <Text fontSize="xs" color={COLORS.dkGray1} textTransform="uppercase" mt={1}>
                Overall Complexity
              </Text>
            </Box>
          </HStack>

          {/* Site & Well Scores */}
          <HStack spacing={8}>
            <VStack spacing={0} align="center">
              <Text fontSize="3xl" fontWeight="700" color={COLORS.white}>
                {Math.round(scores.siteScore * 100)}%
              </Text>
              <HStack spacing={1}>
                <Box w={2} h={2} borderRadius="full" bg={SITE_COLOR} />
                <Text fontSize="xs" color={COLORS.dkGray1}>Site Level</Text>
              </HStack>
            </VStack>
            <VStack spacing={0} align="center">
              <Text fontSize="3xl" fontWeight="700" color={COLORS.white}>
                {Math.round(scores.wellScore * 100)}%
              </Text>
              <HStack spacing={1}>
                <Box w={2} h={2} borderRadius="full" bg={WELL_COLOR} />
                <Text fontSize="xs" color={COLORS.dkGray1}>Well Level</Text>
              </HStack>
            </VStack>
          </HStack>

          {/* Chart Type Switcher */}
          <VStack spacing={2} align="flex-end">
            <ButtonGroup size="xs" isAttached variant="outline">
              <Button
                onClick={() => setChartType('bars')}
                bg={chartType === 'bars' ? COLORS.ltRed : 'transparent'}
                color={COLORS.white}
                borderColor={COLORS.ltGray2}
                _hover={{ bg: chartType === 'bars' ? COLORS.dkRed : COLORS.dkGray2 }}
              >
                Bars
              </Button>
              <Button
                onClick={() => setChartType('radar')}
                bg={chartType === 'radar' ? COLORS.ltRed : 'transparent'}
                color={COLORS.white}
                borderColor={COLORS.ltGray2}
                _hover={{ bg: chartType === 'radar' ? COLORS.dkRed : COLORS.dkGray2 }}
              >
                Radar
              </Button>
              <Button
                onClick={() => setChartType('donut')}
                bg={chartType === 'donut' ? COLORS.ltRed : 'transparent'}
                color={COLORS.white}
                borderColor={COLORS.ltGray2}
                _hover={{ bg: chartType === 'donut' ? COLORS.dkRed : COLORS.dkGray2 }}
              >
                Donut
              </Button>
            </ButtonGroup>
            <FormControl display="flex" alignItems="center" w="auto" mt="2px">
              <FormLabel htmlFor="animation-toggle" fontSize="xs" color={COLORS.dkGray1} mb={0} mr={2}>
                Animate
              </FormLabel>
              <Switch
                id="animation-toggle"
                size="sm"
                colorScheme="red"
                isChecked={animationsEnabled}
                onChange={(e) => setAnimationsEnabled(e.target.checked)}
              />
            </FormControl>
          </VStack>
        </Flex>
      </Box>

      {/* Chart Area */}
      <Flex h="calc(100% - 120px)" p={6} gap={6}>
        {/* Main Chart */}
        <Box flex={3} minW={0}>
          {chartType === 'bars' && renderBarChart()}
          {chartType === 'radar' && renderRadarChart()}
          {chartType === 'donut' && renderDonutChart()}
        </Box>

        {/* Side Panel - Detailed Scores with Dials */}
        <VStack
          flex={1}
          minW="220px"
          spacing={4}
          align="stretch"
          borderLeft="1px solid"
          borderColor={COLORS.ltGray2}
          pl={4}
          display={{ base: 'none', xl: 'flex' }}
        >
          <Text fontSize="xs" fontWeight="600" color={COLORS.dkGray2} textTransform="uppercase">
            Breakdown
          </Text>

          <Box>
            <Text fontSize="sm" color={SITE_COLOR} mb={3} fontWeight="600">SITE</Text>
            <HStack spacing={4} justify="center">
              <Dial value={scores.locationScore} label="Location" color={SITE_COLOR} size={90} />
              <Dial value={scores.rigScore} label="Rig" color={SITE_COLOR} size={90} />
            </HStack>
          </Box>

          <Box>
            <Text fontSize="sm" color={WELL_COLOR} mb={3} fontWeight="600">WELL</Text>
            <VStack spacing={4}>
              <HStack spacing={4} justify="center">
                <Dial value={scores.dsgnScore} label="Design" color={WELL_COLOR} size={90} />
                <Dial value={scores.cnstrctnScore} label="Construction" color={WELL_COLOR} size={90} />
              </HStack>
              <HStack spacing={4} justify="center">
                <Dial value={scores.drlgCmplxScore} label="Drilling" color={WELL_COLOR} size={90} />
                <Dial value={scores.wllCtrlScore} label="Well Ctrl" color={WELL_COLOR} size={90} />
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Flex>
    </Box>
  );
});

export default ScoreChart;
